// hooks/useSalesByTimeData.ts
import { useQuery } from "@tanstack/react-query";
import { getWholesaleRevenueOverTime, getWholesaleOrdersOverTime, getWholesaleUnitsOverTime } from "../services/wholesaleSalesByTimeService";

type MonthSummary = {
  month: number;
  monthName: string;
  currentYear: {
    month: number;
    monthName: string;
    revenue: number;
    orders: number;
    units: number;
  };
  previousYear: {
    month: number;
    monthName: string;
    revenue: number;
    orders: number;
    units: number;
  };
  growth: {
    revenue: number;
    orders: number;
    units: number;
  };
};

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const getMonthName = (monthIndex: number) => MONTH_NAMES[monthIndex - 1];

const calculateGrowth = (prev: number, curr: number) =>
  prev === 0 ? (curr === 0 ? 0 : 1) : (curr - prev) / prev;

export const useWholesaleSalesByTime = () => {
  const revenueQuery = useQuery({
    queryKey: ["wholesale-sales", "wholesale-revenueOverTime"],
    queryFn: getWholesaleRevenueOverTime,
    staleTime: 1000 * 60 * 5, 
  });

  const ordersQuery = useQuery({
    queryKey: ["wholesale-sales", "wholesale-ordersOverTime"],
    queryFn: getWholesaleOrdersOverTime,
    staleTime: 1000 * 60 * 5, 
  });

  const unitsQuery = useQuery({
    queryKey: ["wholesale-sales", "wholesale-unitsOverTime"],
    queryFn: getWholesaleUnitsOverTime,
    staleTime: 1000 * 60 * 5, 
  });

  const isLoading = revenueQuery.isLoading || ordersQuery.isLoading || unitsQuery.isLoading;
  const isError = revenueQuery.isError || ordersQuery.isError || unitsQuery.isError;

  let monthlySummary: MonthSummary[] = [];

  if (!isLoading && !isError) {
    const combinedMap = new Map<string, { revenue?: number; orders?: number; units?: number }>();

    revenueQuery.data.forEach((r: any) => {
      combinedMap.set(r.period, { ...combinedMap.get(r.period), revenue: r.wholesale_revenue });
    });

    ordersQuery.data.forEach((o: any) => {
      combinedMap.set(o.period, { ...combinedMap.get(o.period), orders: o.order_count });
    });

    unitsQuery.data.forEach((u: any) => {
      combinedMap.set(u.period, { ...combinedMap.get(u.period), units: u.units_sold });
    });

    const currentYear = new Date().getFullYear();
    const byMonth: Record<number, any> = {};

    Array.from(combinedMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([period, values]) => {
        const [yearStr, monthStr] = period.split("-");
        const year = parseInt(yearStr, 10);
        const month = parseInt(monthStr, 10);
        const monthName = getMonthName(month);

        const base = byMonth[month] ?? {
          month,
          monthName,
          currentYear: { month, monthName, revenue: 0, orders: 0, units: 0 },
          previousYear: { month, monthName, revenue: 0, orders: 0, units: 0 },
        };

        if (year === currentYear) {
          base.currentYear.revenue = values.revenue ?? 0;
          base.currentYear.orders = values.orders ?? 0;
          base.currentYear.units = values.units ?? 0;
        } else if (year === currentYear - 1) {
          base.previousYear.revenue = values.revenue ?? 0;
          base.previousYear.orders = values.orders ?? 0;
          base.previousYear.units = values.units ?? 0;
        }

        byMonth[month] = base;
      });

    monthlySummary = Object.values(byMonth).map((entry) => ({
      ...entry,
      growth: {
        revenue: calculateGrowth(entry.previousYear.revenue, entry.currentYear.revenue),
        orders: calculateGrowth(entry.previousYear.orders, entry.currentYear.orders),
        units: calculateGrowth(entry.previousYear.units, entry.currentYear.units),
      },
    }));
  }

  return {
    monthlySummary,
    isLoading,
    isError,
  };
};
